import React from "react";
import { Card } from "../../features/ui/card";
import { Skeleton } from "../../features/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../features/ui/table";

interface AccountingLoaderProps {
    variant?: "revenue-ovr" | "expense-rpt" | "payment-card" | "payment-table" | "financial-sum" | "payout" | "tax" | "vat";
    className?: string;
    itemCount?: number; // Optional: customizable item count
}


const AccountingLoader: React.FC<AccountingLoaderProps> = ({ variant = "list", className = "" }) => {
    switch (variant) {
        case "revenue-ovr":
            return (

                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6 pt-6 space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-60 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-60 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-4">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                </div>

            )

        case "expense-rpt":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex justify-center items-center h-[310px] m-6">
                            <div className="relative">
                                <Skeleton className="w-[200px] h-[200px] rounded-full bg-secondary" />
                            </div>
                        </div>
                        <div className="flex flex-row justify-center space-x-3 m-2">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="flex flex-row items-center space-x-1">
                                    <Skeleton className="h-4 w-4 rounded-full bg-secondary" />
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 text-center">
                            <Skeleton className="h-6 w-44 mx-auto rounded bg-secondary" />
                        </div>
                    </div>
                </Card>

            )

        case "payment-card":
            return (
                <div className="h-full flex flex-col">
                    <Card className="p-6 flex-1 flex items-center justify-center">
                        <Skeleton className="h-12 w-40 rounded bg-secondary" />
                    </Card>
                </div>
            )

        case "payment-table":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Invoice ID</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Issue Date</TableHead>
                                <TableHead>Due Date</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-32 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-6 w-20 rounded-full bg-secondary" />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Skeleton className="h-4 w-16 rounded bg-secondary" />
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow className="bg-muted/50">
                                <TableCell className="font-bold" colSpan={5}>
                                    <Skeleton className="h-4 w-40 rounded bg-secondary" />
                                </TableCell>
                                <TableCell className="text-right font-bold">
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            )

        case "financial-sum":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className="space-y-4 m-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="flex justify-between items-center">
                                <Skeleton className="h-4 w-32 bg-secondary" />
                                <Skeleton className="h-4 w-24 bg-secondary" />
                            </div>
                        ))}

                        <div className="pt-4 border-t-2 border-black/20">
                            <div className="flex justify-between items-center">
                                <Skeleton className="h-4 w-32 bg-secondary" />
                                <Skeleton className="h-4 w-20 bg-secondary" />
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <Skeleton className="h-4 w-32 bg-secondary" />
                            <Skeleton className="h-4 w-24 bg-secondary" />
                        </div>
                    </div>
                </Card>
            )

        case "payout":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Channel</TableHead>
                                <TableHead>Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-32 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>

                                </TableRow>
                            ))}
                            <TableRow >
                                <TableCell className="font-bold" colSpan={3}>
                                    <Skeleton className="h-4 w-28 rounded bg-secondary" />
                                </TableCell>
                                <TableCell className="font-bold mx-2">
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            )
        case "tax":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead colSpan={4}>State</TableHead>
                                <TableHead>Tax Rate</TableHead>
                                <TableHead>Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell colSpan={3}>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>

                                </TableRow>
                            ))}
                            <TableRow className="justify-end">
                                <TableCell className="font-bold" colSpan={5}>
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                                <TableCell className="font-bold">
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            )
        case "vat":
            return (
                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead colSpan={4}>Country</TableHead>
                                <TableHead>VAT Rate</TableHead>
                                <TableHead>Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell colSpan={3}>
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                    </TableCell>

                                </TableRow>
                            ))}
                            <TableRow className="justify-end">
                                <TableCell className="font-bold" colSpan={5}>
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                                <TableCell className="font-bold">
                                    <Skeleton className="h-4 w-20 rounded bg-secondary" />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            )
    }

}

export default AccountingLoader